import { z } from 'zod';
import apiClient from '../lib/apiClient';

export const ChannelPlanStatusEnum = z.enum(['ACTIVE', 'GRACE', 'SUSPENDED']);
export type ChannelPlanStatus = z.infer<typeof ChannelPlanStatusEnum>;

export const ChannelPlansPageSchema = z.object({
  items: z.array(
    z.object({
      id: z.number(),
      userId: z.string(),
      status: ChannelPlanStatusEnum,
      endDate: z.string().nullable(),
      channel: z
        .object({
          id: z.number(),
          nameChannel: z.string().nullable(),
        })
        .nullable(),
      plan: z.object({
        id: z.number(),
        name: z.string(),
      }),
    })
  ),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});

export type ChannelPlansPage = z.infer<typeof ChannelPlansPageSchema>;
export type ChannelPlanRow = ChannelPlansPage['items'][number];

export interface GetChannelPlansParams {
  page: number;
  pageSize: number;
  status?: ChannelPlanStatus;
}

/**
 * Fetches a paginated, optionally status-filtered list of channel plans
 * for the backoffice admin table.
 */
export async function getChannelPlans(params: GetChannelPlansParams): Promise<ChannelPlansPage> {
  const query = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
  });
  if (params.status) {
    query.set('status', params.status);
  }

  const data = await apiClient.get(`/backoffice/channel-plans?${query.toString()}`);
  const parsed = ChannelPlansPageSchema.safeParse(data);

  if (!parsed.success) {
    console.error('Invalid channel-plans response:', parsed.error);
    throw new Error('Invalid channel-plans response');
  }

  return parsed.data;
}

/**
 * Confirms a manual renewal for a channel plan currently in GRACE or
 * SUSPENDED status. The backend rejects this call with a 409 when the
 * target plan is already ACTIVE.
 */
export async function confirmManualRenewal(channelPlanId: number) {
  return apiClient.post('/payments/confirm-manual-renewal', { channelPlanId });
}
